import { findStops } from './replayUtils';
import { parseWKT, pointInGeofence } from '@/lib/geofenceGeometry';
import { findBestPOI } from '@/lib/poiMatcher';

/** Map a ms timestamp to 0..1 across [t0, t1], clamped. */
export function toProgress(t, t0, t1) {
  const span = t1 - t0;
  if (span <= 0) return 0;
  const p = (t - t0) / span;
  if (p < 0) return 0;
  if (p > 1) return 1;
  return p;
}

/** Human duration: "4m", "1h 3m". */
function fmtDur(ms) {
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** Stops → normalized 'stop' events (reuses the existing findStops). */
export function eventsFromStops(positions, minStopMs = 120000) {
  return findStops(positions, minStopMs).map((s) => {
    const tStart = Date.parse(s.startTime);
    const tEnd = Date.parse(s.endTime);
    return {
      kind: 'stop',
      tStart, tEnd,
      lat: s.latitude, lng: s.longitude,
      label: `Stopped ${fmtDur(s.duration)}`,
      severity: 'info',
      durationMs: s.duration,
      detail: '',
    };
  });
}

// event_type → severity. Default 'warn'; a small high-severity set is 'critical'.
const CRITICAL_TYPES = new Set([
  'sos', 'deviceOverspeed', 'overspeed', 'enterHighRiskArea', 'powerCut', 'sosUnacknowledged',
]);

/** rule_events rows → normalized 'alert' events. */
export function mapRuleEvents(rows = []) {
  const out = [];
  for (const r of rows) {
    const pd = r.position_data || {};
    const lat = pd.latitude ?? pd.lat;
    const lng = pd.longitude ?? pd.lng;
    if (lat == null || lng == null) continue; // staff/no-coord rows skipped
    const t = Date.parse(r.triggered_at);
    out.push({
      kind: 'alert',
      tStart: t, tEnd: t,
      lat, lng,
      label: `Alert: ${r.event_type}`,
      severity: CRITICAL_TYPES.has(r.event_type) ? 'critical' : 'warn',
      eventType: r.event_type,
      durationMs: 0,
      detail: r.event_type,
    });
  }
  return out;
}

const KMH = 1.852; // knots → km/h

/** Contiguous runs where speed exceeds limitKmh → one 'overspeed' event each. */
export function findOverspeedEvents(positions, { limitKmh = 100 } = {}) {
  const out = [];
  let run = null;
  for (const p of positions) {
    const kmh = (p.speed || 0) * KMH;
    if (kmh > limitKmh) {
      if (!run) run = { start: p, end: p, max: kmh, peakPos: p };
      else {
        run.end = p;
        if (kmh > run.max) { run.max = kmh; run.peakPos = p; }
      }
    } else if (run) {
      out.push(overspeedEvent(run));
      run = null;
    }
  }
  if (run) out.push(overspeedEvent(run));
  return out;
}

function overspeedEvent(run) {
  return {
    kind: 'overspeed',
    tStart: Date.parse(run.start.fixTime),
    tEnd: Date.parse(run.end.fixTime),
    lat: run.peakPos.latitude, lng: run.peakPos.longitude,
    label: `Overspeed ${Math.round(run.max)} km/h`,
    severity: 'warn',
    durationMs: Date.parse(run.end.fixTime) - Date.parse(run.start.fixTime),
    detail: `${Math.round(run.max)} km/h`,
  };
}

/** Enter/exit crossings for each geofence, by edge-detecting membership along the track. */
export function findGeofenceCrossings(positions, geofences = []) {
  const out = [];
  for (const g of geofences) {
    const shape = parseWKT(g.area);
    if (!shape) continue;
    let prevInside = false;
    positions.forEach((p, i) => {
      const inside = pointInGeofence(p.latitude, p.longitude, shape);
      if (i > 0 && inside !== prevInside) {
        const t = Date.parse(p.fixTime);
        out.push({
          kind: 'geofence',
          tStart: t, tEnd: t,
          lat: p.latitude, lng: p.longitude,
          label: `${inside ? 'Entered' : 'Exited'} geofence: ${g.name}`,
          severity: 'info',
          durationMs: 0,
          detail: `${inside ? 'Entered' : 'Exited'}: ${g.name}`,
        });
      }
      prevInside = inside;
    });
  }
  return out;
}

/** One 'poi' event per contiguous in-radius run, using the existing matcher. */
export function findPoiVisits(positions, pois = [], { orgId, sources = { org: true, global: true } } = {}) {
  const out = [];
  let run = null;
  for (const p of positions) {
    const poi = findBestPOI(p.latitude, p.longitude, pois, { sources, orgId });
    const name = poi?.name ?? null;
    if (name) {
      if (!run || run.name !== name) {
        if (run) out.push(poiEvent(run));
        run = { name, start: p, end: p, lat: p.latitude, lng: p.longitude };
      } else {
        run.end = p;
      }
    } else if (run) {
      out.push(poiEvent(run));
      run = null;
    }
  }
  if (run) out.push(poiEvent(run));
  return out;
}

function poiEvent(run) {
  return {
    kind: 'poi',
    tStart: Date.parse(run.start.fixTime),
    tEnd: Date.parse(run.end.fixTime),
    lat: run.lat, lng: run.lng,
    label: `Visited POI: ${run.name}`,
    severity: 'info',
    durationMs: Date.parse(run.end.fixTime) - Date.parse(run.start.fixTime),
    detail: run.name,
  };
}

const HARSH_ACCEL_KMH_PER_S = 8;   // hard acceleration
const HARSH_BRAKE_KMH_PER_S = 9;   // hard braking (slightly higher bar — brake spikes are noisier)
const HARSH_MAX_GAP_S = 30;        // ignore deltas across long gaps (not a real maneuver)

/** Compute harsh accel/brake from Δspeed/Δt between consecutive fixes. */
export function findHarshEvents(positions) {
  const out = [];
  for (let i = 1; i < positions.length; i++) {
    const a = positions[i - 1];
    const b = positions[i];
    const dt = (Date.parse(b.fixTime) - Date.parse(a.fixTime)) / 1000;
    if (dt <= 0 || dt > HARSH_MAX_GAP_S) continue;
    const dv = ((b.speed || 0) - (a.speed || 0)) * KMH; // km/h
    const rate = dv / dt; // km/h per second
    if (rate >= HARSH_ACCEL_KMH_PER_S) {
      out.push(harshEvent(b, 'accel', rate));
    } else if (rate <= -HARSH_BRAKE_KMH_PER_S) {
      out.push(harshEvent(b, 'brake', rate));
    }
  }
  return out;
}

function harshEvent(pos, type, rate) {
  const t = Date.parse(pos.fixTime);
  const r = Math.round(rate);
  return {
    kind: 'harsh',
    tStart: t, tEnd: t,
    lat: pos.latitude, lng: pos.longitude,
    label: `Harsh ${type === 'brake' ? 'braking' : 'acceleration'} (${Math.abs(r)} km/h/s)`,
    severity: 'warn',
    durationMs: 0,
    detail: `${r > 0 ? '+' : ''}${r} km/h/s`,
  };
}

/**
 * Merge all signal sources into one normalized, progress-stamped, sorted array.
 */
export function buildReplayEvents({
  positions, ruleEvents = [], geofences = [], pois = [], orgId,
  overspeedLimitKmh = 100,
}) {
  if (!positions || positions.length < 2) return [];
  const t0 = Date.parse(positions[0].fixTime);
  const t1 = Date.parse(positions[positions.length - 1].fixTime);

  const raw = [
    ...eventsFromStops(positions),
    ...mapRuleEvents(ruleEvents),
    ...findOverspeedEvents(positions, { limitKmh: overspeedLimitKmh }),
    ...findGeofenceCrossings(positions, geofences),
    ...findPoiVisits(positions, pois, { orgId }),
    ...findHarshEvents(positions),
  ];

  raw.sort((a, b) => a.tStart - b.tStart);
  // Stable id derived from kind + tStart (NOT the sort-order index): the alert
  // fetch resolves async, so re-running with more sources must not renumber
  // existing events — that would break activeId tracking + churn React keys.
  // A per-(kind,tStart) counter disambiguates the rare same-ms collision.
  const seen = new Map();
  return raw.map((e) => {
    const base = `${e.kind}-${e.tStart}`;
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    // POI events already carry their POI name in `detail`; only the other
    // kinds need the matcher to discover which POI (if any) they sit inside.
    const poiName = e.kind === 'poi'
      ? e.detail
      : (findBestPOI(e.lat, e.lng, pois, { sources: { org: true, global: true }, orgId })?.name ?? null);
    return {
      ...e,
      id: n === 0 ? base : `${base}-${n}`,
      progress: toProgress(e.tStart, t0, t1),
      poiName,
    };
  });
}
